import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

// A 60% copy of the game's BannerItem.ts: a 740 x 217 canvas, the image fitted inside it from the
// top left, and the same fonts, sizes and wrap widths, so text wraps where it wraps in the game.
const SCALE = 0.6;
const px = (value: number) => `${value * SCALE}px`;

// The files the game loads for commonFont, commonFontBold, commonFont4 and commonFont2.
const FONT_DIR = `${import.meta.env.BASE_URL}canvas/assets/fonts/en/`;
const BANNER_FONTS = [
  ["PBF Banner Title", "Degular-Medium.ttf"],
  ["PBF Banner Title Bold", "Degular-Bold.ttf"],
  ["PBF Banner Body", "avenir-next-regular.ttf"],
  ["PBF Banner Body Bold", "AvenirNext-Bold.ttf"],
];

// In game pixels. lineHeight is how Pixi measures a line (ascent + descent of "|ÉqÅM", plus the
// body's leading of 6); top moves the CSS baseline onto Pixi's.
const TEXT_STYLES = {
  title: { family: "PBF Banner Title", size: 48, left: 56, top: 33.5, lineHeight: 50 },
  titleBold: { family: "PBF Banner Title Bold", size: 54, left: 54, top: 34.5, lineHeight: 57 },
  body: { family: "PBF Banner Body", size: 30, left: 54, top: 92, lineHeight: 43 },
  bodyBold: { family: "PBF Banner Body Bold", size: 30, left: 56, top: 93, lineHeight: 43 },
};

let bannerFontsRequested = false;
const loadBannerFonts = () => {
  if (bannerFontsRequested) return;
  bannerFontsRequested = true;
  BANNER_FONTS.forEach(([family, file]) => {
    new FontFace(family, `url("${FONT_DIR}${file}")`)
      .load()
      .then((face) => document.fonts.add(face))
      .catch(() => undefined);
  });
};

const textSx = (
  style: (typeof TEXT_STYLES)[keyof typeof TEXT_STYLES],
  wrapWidth: number,
  color: string,
) => ({
  position: "absolute",
  left: px(style.left),
  top: px(style.top),
  maxWidth: px(wrapWidth),
  color,
  fontFamily: `"${style.family}", sans-serif`,
  fontWeight: "normal",
  fontSize: px(style.size),
  lineHeight: px(style.lineHeight),
  whiteSpace: "pre-wrap",
});

interface BannerPreviewProps {
  imageUrl?: string | null;
  title?: string;
  body?: string;
  textColor?: string;
  isTitleBold?: boolean;
  isBodyBold?: boolean;
}

const BannerPreview = ({
  imageUrl,
  title,
  body,
  textColor,
  isTitleBold,
  isBodyBold,
}: BannerPreviewProps) => {
  const [errored, setErrored] = useState(false);
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(
    null,
  );
  const color = textColor || "#FFFFFF";

  useEffect(() => {
    setErrored(false);
    setImageSize(null);
  }, [imageUrl]);

  useEffect(loadBannerFonts, []);

  const showImage = Boolean(imageUrl) && !errored;
  // The game has nothing behind the banner, so the box hugs the fitted image instead of leaving an
  // empty strip beside it.
  const fitScale =
    showImage && imageSize
      ? Math.min(740 / imageSize.w, 217 / imageSize.h)
      : null;
  const boxWidth = fitScale ? imageSize.w * fitScale : 740;
  const boxHeight = fitScale ? imageSize.h * fitScale : 217;

  return (
    <Box sx={{ width: px(740), maxWidth: "100%", flexShrink: 0 }}>
      <Box
        sx={{
          position: "relative",
          width: px(boxWidth),
          height: px(boxHeight),
          borderRadius: px(8),
          overflow: "hidden",
          background: "linear-gradient(135deg, #1d1030 0%, #3f3350 100%)",
        }}
      >
        {showImage ? (
          <Box
            component="img"
            src={imageUrl as string}
            alt="Banner preview"
            onLoad={(event: React.SyntheticEvent<HTMLImageElement>) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (naturalWidth && naturalHeight) {
                setImageSize({ w: naturalWidth, h: naturalHeight });
              }
            }}
            onError={() => setErrored(true)}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "left top",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              right: px(28),
              bottom: px(18),
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "rgba(255, 255, 255, 0.45)",
            }}
          >
            <ImageOutlinedIcon fontSize="small" />
            <Typography variant="caption">No image selected</Typography>
          </Box>
        )}
        <Typography
          sx={textSx(
            isTitleBold ? TEXT_STYLES.titleBold : TEXT_STYLES.title,
            600,
            color,
          )}
        >
          {title || "Banner title"}
        </Typography>
        <Typography
          sx={{
            ...textSx(
              isBodyBold ? TEXT_STYLES.bodyBold : TEXT_STYLES.body,
              500,
              color,
            ),
            letterSpacing: px(0.2),
          }}
        >
          {body || "Banner body text"}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        In game preview. The banner may look slightly different in the game,
        depending on the device and screen size.
      </Typography>
    </Box>
  );
};

export default BannerPreview;
